@if (asleep()) {
  <!-- Outside opening hours: a black screen, nothing drawn on it. -->
  <div class="sleep"></div>
} @else {
  <!-- The stage: the chosen size (or the window), scaled to the window. Its width picks HD or 4K type. -->
  <div
    class="stage"
    [class.fixed]="!!stage()"
    [style.width.px]="stage()?.width"
    [style.height.px]="stage()?.height"
    [style.transform]="stageTransform()"
  >
    <!-- The zoom: laid out 1/zoom as large and scaled back, so below 100 % more fits. -->
    <div
      class="zoom"
      [style.width.px]="layout().width"
      [style.height.px]="layout().height"
      [style.--screen-w.px]="base().width"
      [style.--screen-h.px]="base().height"
      [style.--space-w.px]="layout().width"
      [style.--space-h.px]="layout().height"
      [style.--decor-top-h.px]="decorTopHeight()"
      [style.transform]="zoomTransform()"
    >
      <!-- A seasonal theme's decorations along the edges, behind everything; empty for the plain themes. -->
      <div
        class="decor"
        aria-hidden="true"
        [style.--rain-drift]="windDrift().rain"
        [style.--rain-slant.deg]="windDrift().rainSlant"
        [style.--light-drift]="windDrift().light"
      >
        <div class="decor-top"></div>
        <div class="decor-right"></div>
        <div class="decor-bottom"></div>
        @if (sky(); as sky) {
          <!-- The sky at the top: the sun by day and the moon by night in an arc, and now and then a cloud. -->
          <div class="sky" [class]="skyWeather()" [style.--wind-factor]="windFactor()">
            <!-- The sun by day; the moon while it is up — at night, and by day too when the forecast says so. -->
            @if (sky.sun; as sun) {
              <div class="sky-body sun" [style.--across]="sun.across" [style.--lift]="sun.lift"></div>
            }
            @if (sky.moon; as moon) {
              <div
                class="sky-body moon"
                [class.phased]="moonShape()"
                [style.--across]="moon.across"
                [style.--lift]="moon.lift"
              >
                @if (moonShape(); as shape) {
                  <!-- The real moon: the whole disc faintly, and its lit part. -->
                  <svg class="phase" viewBox="-50 -50 100 100" aria-hidden="true">
                    <circle class="earthshine" r="50" />
                    @if (shape.lit) {
                      <path class="lit" [attr.d]="shape.lit" />
                    }
                  </svg>
                }
              </div>
            }
            <div class="cloud c1"></div>
            <div class="cloud c2"></div>
            <div class="cloud c3"></div>
            <!-- More clouds, rain, snow and lightning: shown by the weather class, when there is weather. -->
            <div class="cloud c4"></div>
            <div class="cloud c5"></div>
            <div class="precip">
              @for (drop of precipSlots; track $index) {
                <span [style.--x]="drop.x" [style.--d]="drop.delay"></span>
              }
            </div>
          </div>
        }
        @if (drizzle(); as drizzle) {
          <!-- The year theme's sprinkle: a few snowflakes, petals, leaves, sparks or confetti. -->
          <div [class]="drizzle">
            @for (slot of particleSlots; track $index) {
              <span [style.--x]="slot.x" [style.--t]="slot.duration" [style.--d]="slot.delay" [style.--s]="slot.size"></span>
            }
          </div>
        }
      </div>

      <div class="frame">
      @if (feed()?.viewType === 'Room' || feed()?.viewType === 'LockerRoom') {
        <app-room-view
          [periods]="feed()!.periods"
          [roomName]="feed()!.roomName ?? ''"
          [now]="minute()"
          [fields]="feed()!.cardFields"
          [kind]="feed()!.viewType === 'LockerRoom' ? 'locker' : 'meeting'"
        />
      } @else {
        <section class="screen">
          @if (feed()?.showHeader !== false) {
              <app-infoscreen-header
              [date]="now()"
              [spinnerState]="loading() && !feed() ? 'loading' : 'inactive'"
            >
              @if (greeting(); as greeting) {
                <!-- The holiday the screen is decorated for, by the date. -->
                <span dateExtra class="holiday-greeting" [class]="'greeting-' + greeting.holiday">{{ greeting.text }}</span>
              }
              @if (shownWeather(); as weather) {
                <!-- The weather's symbol (the moon at night) and the temperature, under the clock. -->
                <span clockBelow class="clock-temp">
                  <app-weather-icon [condition]="weather.condition" [night]="weather.night" />
                  {{ degrees(weather.temperature) }}
                </span>
              }
            </app-infoscreen-header>
          }
          @if (decorAtTop()) {
            <!-- The decorations at the top only: the bottom band as a divider between the header and the content. -->
            <div #decorDivider class="decor-divider" aria-hidden="true"></div>
          }

          @if (feed(); as feed) {
            @if (feed.messages.length) {
              <app-message-bar [messages]="feed.messages" />
            }

            <div
              class="body"
              [class.split]="split()"
              [class.pane-left]="feed.splitSide === 'Left'"
              [style.--pane-width]="feed.splitPercent + '%'"
            >
              <div class="main" [class.weather-paged]="weatherPageShown() && feed.weatherMode === 'Page' && !!shownWeather()">
              @switch (feed.viewType) {
                @case ('Welcome') {
                  <app-welcome-pane
                    [text]="feed.welcomeText"
                    [body]="feed.welcomeBody ?? null" [styling]="feed"
                    [ads]="showcase()"
                    [speed]="feed.adScrollSpeed"
                    [weather]="shownWeather()"
                    [news]="feed.news"
                    [newsSource]="feed.newsSourceName"
                  />
                }
                @case ('List') {
                  <app-list-view
                    [periods]="feed.periods"
                    [now]="minute()"
                    [speed]="feed.listScrollSpeed"
                    [mode]="feed.listMode"
                    [pageSeconds]="feed.pageSeconds"
                    [columns]="feed.listColumns"
                    (scrollingChange)="listScrolling.set($event)"
                  />
                }
                @default {
                  <app-calendar-view
                    [periods]="feed.periods"
                    [viewType]="feed.viewType"
                    [from]="feed.from"
                    [now]="minute()"
                    [pageSeconds]="feed.pageSeconds"
                    [cardFields]="feed.cardFields"
                  />
                }
              }
              @if (feed.weatherMode === 'Page' && feed.viewType !== 'Welcome' && shownWeather()) {
                @if (shownWeather(); as weather) {
                  <!-- The forecast as a page, faded in over the view now and then. -->
                  <app-weather-page class="weather-overlay" [class.shown]="weatherPageShown()" [weather]="weather" />
                }
              }
              </div>
              @if (split()) {
                <!-- The split screen's other half: the welcome text and the ads. -->
                <app-welcome-pane
                  class="pane"
                  [text]="feed.welcomeText"
                    [body]="feed.welcomeBody ?? null" [styling]="feed"
                  [ads]="showcase()"
                  [speed]="feed.adScrollSpeed"
                  [weather]="shownWeather()"
                  [news]="feed.news"
                  [newsSource]="feed.newsSourceName"
                  [compact]="true"
                />
              }
            </div>

            @if (ticker()) {
              <app-news-ticker [items]="feed.news" [source]="feed.newsSourceName" />
            }

            <!-- The ad band, unless the ads have a pane (or the whole screen) of their own. -->
            @if (bandAds().length && !split() && feed.viewType !== 'Welcome') {
              <app-ad-band [ads]="bandAds()" [speed]="feed.adScrollSpeed" [paged]="listScrolls()" [weather]="shownWeather()" />
            }

            <app-infoscreen-footer class="footer-band" [locationName]="footerText()" />
          } @else {
            <div class="body error">{{ error() }}</div>
            <app-infoscreen-footer class="footer-band" />
          }
        </section>
      }
      </div>

      @if (demoBadge(); as badge) {
        <!-- A demo screen says which set-up it shows, and with the clock how soon the next one comes —
             in a rotating demo the round's badge has the clock, as the set-up changes with the screen. -->
        <div class="demo-badge">
          Demo · {{ badge.label }}
          @if (demoTurn() === null) {
            <app-page-pie [index]="badge.index" [count]="demoCount" [seconds]="demoSeconds" [elapsed]="badge.elapsed" />
          }
        </div>
      }

      <!-- In front of everything, for what passes by now and then (the Easter bunny); empty otherwise. -->
      <div class="passer-by" aria-hidden="true"></div>
    </div>
  </div>
}
